import { useEffect, useRef, useState } from "react";

import { Trash } from "phosphor-react";

import { Text } from "@runwayhealth/runway-components-react";
import { Condition } from "@types-runway/condition";
import { RemovedMedication } from "@types-runway/removedMedication";
import { medicationRequiresLabExam } from "@utils/labExam/labExam";
import { labExamCopy } from "@utils/labExam/labExamCopy";

import { RemoveMedicationDialog } from "./RemoveMedicationDialog";
import { describeRemovedMedication, RemovedMedicationRows } from "./RemovedMedicationRows";

interface MedicationSummaryRowsProps {
  conditions: Condition[];
  isLoadingSummary: boolean;
  /** Whether the order is still editable. Rows show no remove button when it is not. */
  canRemove: boolean;
  /** Whether a save or payment is in progress. Remove buttons stay focusable but do nothing. */
  isBusy: boolean;
  onRemove: (productName: string) => void;
  /** Medications taken out of this order that the patient can still put back. */
  removedMedications: RemovedMedication[];
  onRestore: (productName: string) => void;
}

const formatPrice = (cents: number) => (cents / 100).toFixed(0);

const describeMedication = (condition: Condition) => {
  const medicationName = condition.suggestedMedication?.name;
  return medicationName ? `${medicationName} (${condition.product.name})` : condition.product.name;
};

export const MedicationSummaryRows = ({
  conditions,
  isLoadingSummary,
  canRemove,
  isBusy,
  onRemove,
  removedMedications,
  onRestore,
}: MedicationSummaryRowsProps) => {
  const [conditionToRemove, setConditionToRemove] = useState<Condition | null>(null);
  const [removingName, setRemovingName] = useState<string | null>(null);
  const [failedName, setFailedName] = useState<string | null>(null);
  const [restoringName, setRestoringName] = useState<string | null>(null);
  const [failedRestoreName, setFailedRestoreName] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const groupRef = useRef<HTMLDivElement>(null);
  const requestedRemovalRef = useRef<string | null>(null);
  const requestedRestoreRef = useRef<{
    productName: string;
    medicationName: string;
  } | null>(null);
  const restorableMedications = removedMedications.filter(
    (removed) =>
      !conditions.some((condition) => condition.product.name === removed.condition.product.name)
  );

  const focusByAttribute = (attribute: string, value: string) =>
    Array.from(
      groupRef.current?.parentElement?.querySelectorAll<HTMLElement>(`[${attribute}]`) ?? []
    )
      .find((element) => element.getAttribute(attribute) === value)
      ?.focus();

  useEffect(() => {
    const requestedName = requestedRemovalRef.current;
    const isRequestedNameGone = !conditions.some(
      (condition) => condition.product.name === requestedName
    );

    if (!requestedName || !isRequestedNameGone) return;

    requestedRemovalRef.current = null;
    setAnnouncement(
      `${requestedName} removed from your order. You can add it back under Removed from your order.`
    );
    // The removed row's button no longer exists, so keyboard focus moves to the list.
    groupRef.current?.focus();
  }, [conditions]);

  useEffect(() => {
    const requestedRestore = requestedRestoreRef.current;
    const isRestored = conditions.some(
      (condition) => condition.product.name === requestedRestore?.productName
    );

    if (!requestedRestore || !isRestored) return;

    requestedRestoreRef.current = null;
    setAnnouncement(`${requestedRestore.medicationName} added back to your order.`);
    focusByAttribute("data-product-name", requestedRestore.productName);
  }, [conditions]);

  useEffect(() => {
    if (isBusy) return;

    setRemovingName(null);
    setRestoringName(null);

    const requestedRestore = requestedRestoreRef.current;
    if (requestedRestore) {
      // A save that finished without restoring the row failed; the patient may retry.
      requestedRestoreRef.current = null;
      setFailedRestoreName(requestedRestore.productName);
      focusByAttribute("data-restore-name", requestedRestore.productName);
    }

    const requestedName = requestedRemovalRef.current;
    if (!requestedName) return;

    // A save that finished without removing the row failed; the patient may retry.
    requestedRemovalRef.current = null;
    setFailedName(requestedName);
    Array.from(groupRef.current?.querySelectorAll<HTMLElement>("[data-product-name]") ?? [])
      .find((button) => button.dataset.productName === requestedName)
      ?.focus();
  }, [isBusy]);

  const handleChoice = (isConfirmed: boolean) => {
    const condition = conditionToRemove;
    setConditionToRemove(null);

    if (!isConfirmed || !condition) return;

    requestedRemovalRef.current = condition.product.name;
    setFailedName(null);
    setRemovingName(condition.product.name);
    onRemove(condition.product.name);
  };

  const handleRestore = (productName: string) => {
    const removed = removedMedications.find(
      (entry) => entry.condition.product.name === productName
    );
    if (!removed) return;

    requestedRestoreRef.current = {
      productName,
      medicationName: describeRemovedMedication(removed),
    };
    setFailedRestoreName(null);
    setRestoringName(productName);
    onRestore(productName);
  };

  const handleRemoveClick = (condition: Condition) => {
    if (isBusy) return;

    setConditionToRemove(condition);
  };

  const buildConfirmationMessage = (condition: Condition) => {
    const visitFeeNotice = conditions.length === 1 ? " Your visit fee still applies." : "";
    return `${describeMedication(condition)} will be removed from your order.${visitFeeNotice} Your consultation answers are not affected.`;
  };

  return (
    <>
      <div
        ref={groupRef}
        tabIndex={-1}
        role="group"
        aria-label="Medications due only if prescribed"
      >
        {conditions.map((condition, index) => {
          const productName = condition.product.name;
          const isRemoving = isBusy && removingName === productName;
          return (
            <div className="treatment-row" key={productName}>
              <div className="treatment-fee">
                <div className="treatment-fee-name">
                  {isLoadingSummary ||
                    (!condition.suggestedMedication?.id && (
                      <div data-testid={`missing-suggestion-${index}`}>...</div>
                    ))}
                  <Text size="md">{productName}</Text>
                  <Text size="sm" as="span">
                    {condition.suggestedMedication?.name}
                  </Text>
                  {condition.suggestedMedication &&
                    medicationRequiresLabExam(condition.suggestedMedication) && (
                      <span className="lab-exam-tag">{labExamCopy.checkoutTag}</span>
                    )}
                </div>
                <div className="treatment-fee-end">
                  <Text size="md" data-testid={`medication-price-${index}`}>
                    ${formatPrice(condition.price || 0)}
                  </Text>
                  {canRemove && (
                    <button
                      type="button"
                      className="medication-action-button"
                      data-testid={`remove-medication-${index}`}
                      data-product-name={productName}
                      aria-label={`Remove ${describeMedication(condition)}`}
                      aria-disabled={isBusy}
                      aria-busy={isRemoving}
                      onClick={() => handleRemoveClick(condition)}
                    >
                      <Trash aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
              {failedName === productName && (
                <p role="alert" className="remove-medication-error">
                  {`We couldn't remove ${describeMedication(condition)}. Your order has not changed.`}
                </p>
              )}
            </div>
          );
        })}
        <div role="status" className="visually-hidden-status">
          {announcement}
        </div>
        <RemoveMedicationDialog
          isOpen={conditionToRemove !== null}
          title="Remove medication?"
          message={conditionToRemove ? buildConfirmationMessage(conditionToRemove) : ""}
          isBusy={isBusy}
          onCancel={() => handleChoice(false)}
          onConfirm={() => handleChoice(true)}
        />
      </div>
      {canRemove && (
        <RemovedMedicationRows
          removedMedications={restorableMedications}
          isBusy={isBusy}
          restoringName={restoringName}
          failedName={failedRestoreName}
          onRestore={handleRestore}
        />
      )}
    </>
  );
};

export default MedicationSummaryRows;
